/**
 * The investigation watch model — what a case file is *following*.
 *
 * An investigation is two things at once: a **watch scope** (this module) and a
 * collaborative **case file** (the `@authe/workspace` shell on the detail page).
 * A {@link Watch} is one thing being followed — a protocol, an account, a
 * catalog row, a chain — and an investigation holds several, so "the Uniswap
 * insider" (their address *and* the pools they touch) is one case rather than
 * three.
 *
 * Watches are **multi-chain by construction**: chains are CAIP-2 (`eip155:1`)
 * and accounts CAIP-10 (`eip155:1:0xabc…`), matching what the backend now
 * stores, and a protocol or account watch spans every chain unless narrowed
 * because a hacker moving funds does not stay on one chain.
 *
 * Each watch resolves two ways, and both must agree or the feed contradicts
 * itself as rows arrive:
 *
 * - {@link watchToSearchTerm} — the backlog, as one term of a
 *   `POST /transactions/search` request. The server routes each term to the index
 *   that can answer it and merges the lanes behind one cursor, so the whole watch
 *   set is a single request with a single page budget.
 * - {@link matchesWatch} — the live tail, as a predicate over the published
 *   stream payload. AppSync has no server-side filtering, so every subscriber
 *   receives every row its channels carry and discards the non-matching ones
 *   client-side.
 */
import { chainDisplayName, normalizeAddress, toStoredChainId } from "@authe/fe-commons/chains";
import {
  PRESET_LABELS,
  PRESET_SECONDS,
  type TimeRangePreset,
} from "@authe/fe-commons/metadata-analytics";
import type { SceneOverlayDTO } from "@authe/fe-commons/models";
import type { TxTenancyScopeId } from "@/features/transactions/scopes";
import type { CaseAct } from "./caseActs";

/** A CAIP-2 chain id, e.g. `eip155:1`. */
export type Caip2 = string;
/** A CAIP-10 account, e.g. `eip155:1:0xabc…`. */
export type Caip10 = string;

/** Catalog kinds a watch can follow (the reverse-index lane on the list API). */
export type WatchCatalogKind = "entity" | "tag" | "label" | "group";

/**
 * One thing an investigation follows.
 *
 * `chains` narrows a watch to specific chains and means the same thing on every
 * kind that carries it: **empty or absent is "do not narrow"**, not "no chains".
 * For a protocol that is every chain it is deployed on; for an account it is
 * every chain in the account's CAIP-2 namespace. Uniform because a scope editor
 * that means "all" in one place and "none" in another is a footgun, and because
 * `watchKey` derives identity from it.
 */
export type Watch =
  | { kind: "protocol"; protocolId: string; chains?: Caip2[] }
  | { kind: "address"; account: Caip10; chains?: Caip2[] }
  | { kind: WatchCatalogKind; catalogId: string; name?: string }
  | { kind: "chain"; chain: Caip2 };

export type WatchKind = Watch["kind"];

/**
 * The entity this case is about, when it is about one.
 *
 * The Entity / org watch template seeds exactly one entity watch, and in that
 * case nearly every attribution a team makes is "this too belongs to them" — so
 * the surfaces that attribute things offer it as a single click rather than a
 * search. A case following two entities has no such default and gets none: the
 * first of two would be a guess, and a wrong one-click attribution is worse than
 * a picker.
 */
export function watchedEntity(watches: Watch[]): WatchedEntity | undefined {
  const entities = watches.filter((w) => w.kind === "entity");
  if (entities.length !== 1) return undefined;
  const [only] = entities;
  if (!only || only.kind !== "entity") return undefined;
  return { id: only.catalogId, name: only.name ?? only.catalogId };
}

export interface WatchedEntity {
  id: string;
  name: string;
}

/**
 * What a case is ABOUT, derived from what it follows.
 *
 * The gallery's templates are a starting point, not a property of the case: the
 * created investigation keeps the watches and the saga preset and forgets which
 * card seeded them. That is the right way round — a case drifts (a protocol
 * monitor grows two addresses and an entity as the work develops), and a stored
 * template id would then be a claim about the case that is no longer true. The
 * watches are always the truth, so the focus is read from them each time.
 *
 * Three surfaces key off this: which columns a case opens with, which metrics
 * sit above its table, and which faceted filters its toolbar offers.
 */
export type CaseFocus =
  | { kind: "protocol"; protocolIds: string[] }
  | { kind: "entity"; entity: WatchedEntity }
  | { kind: "address"; accounts: Caip10[] }
  | { kind: "saga"; saga: NonNullable<InvestigationSaga> }
  | { kind: "mixed" };

/**
 * Read a case's focus from its watches (and its saga preset, which outranks
 * them — a bridge case is about whether both legs landed, whatever it follows
 * to find them).
 *
 * A case is focused only when its watches name ONE kind of subject. Chain
 * watches don't count: a chain narrows a case rather than naming what it is
 * about, so "this protocol, on Base" is still a protocol case. Anything else is
 * `mixed`, which every consumer treats as "offer the neutral view" — the same
 * refusal to guess as {@link watchedEntity}, and for the same reason. A case
 * following a protocol AND two entities has no single best set of columns, and
 * picking one would quietly hide the columns the other half of the work needs.
 */
export function caseFocus(watches: Watch[], saga: InvestigationSaga = null): CaseFocus {
  if (saga) return { kind: "saga", saga };

  const subjects = watches.filter((w) => w.kind !== "chain");
  const kinds = new Set(subjects.map((w) => w.kind));
  if (kinds.size !== 1) return { kind: "mixed" };

  switch ([...kinds][0]) {
    case "protocol":
      return { kind: "protocol", protocolIds: distinct(subjects, (w) => (w.kind === "protocol" ? w.protocolId : undefined)) };
    case "address":
      return { kind: "address", accounts: distinct(subjects, (w) => (w.kind === "address" ? w.account : undefined)) };
    case "entity": {
      const entity = watchedEntity(watches);
      return entity ? { kind: "entity", entity } : { kind: "mixed" };
    }
    default:
      // A tag / label / group watch is a real subject, but nothing on the page
      // specialises for one yet, so it reads as the neutral view rather than
      // pretending otherwise.
      return { kind: "mixed" };
  }
}

function distinct(watches: Watch[], pick: (watch: Watch) => string | undefined): string[] {
  const out = new Set<string>();
  for (const watch of watches) {
    const value = pick(watch);
    if (value) out.add(value);
  }
  return [...out].sort();
}

/** The chains a watch is narrowed to, sorted, or `[]` for "every chain". */
export function watchChains(watch: Watch): Caip2[] {
  if (watch.kind === "chain") return [watch.chain];
  if (watch.kind === "protocol" || watch.kind === "address") {
    return [...(watch.chains ?? [])].sort();
  }
  return [];
}

/**
 * Stable identity for a watch — React keys, de-duplication when two templates
 * seed the same thing, and per-lane cursor scoping in the feed.
 */
export function watchKey(watch: Watch): string {
  switch (watch.kind) {
    case "protocol":
      return `protocol:${watch.protocolId}:${watchChains(watch).join(",")}`;
    case "address":
      // The chain narrowing is part of the identity, so following an account
      // namespace-wide and following it on one chain are two watches rather
      // than one silently overwriting the other in `withWatch`.
      return `address:${watch.account}:${watchChains(watch).join(",") || "ns"}`;
    case "chain":
      return `chain:${watch.chain}`;
    default:
      return `${watch.kind}:${watch.catalogId}`;
  }
}

/** Split a CAIP-10 account into its chain and address halves. */
export function parseAccount(account: Caip10): { chain: Caip2 | null; address: string } {
  const cut = account.lastIndexOf(":");
  if (cut <= 0) return { chain: null, address: account };
  return { chain: account.slice(0, cut), address: account.slice(cut + 1) };
}

/** Join a chain and address into a CAIP-10 account (chainless → bare address). */
export function toAccount(chain: Caip2 | null | undefined, address: string): Caip10 {
  const canonical = toStoredChainId(chain);
  return canonical ? `${canonical}:${address}` : address;
}

/** The namespace half of a CAIP-2 id (`eip155:1` → `eip155`). */
export function caip2Namespace(chain: Caip2 | null | undefined): string | null {
  if (!chain) return null;
  const cut = chain.indexOf(":");
  return cut > 0 ? chain.slice(0, cut) : chain;
}

/** Human label for a watch chip. */
export function watchLabel(watch: Watch): string {
  switch (watch.kind) {
    case "protocol":
      return watch.protocolId;
    case "address": {
      const { address } = parseAccount(watch.account);
      return address.length > 14 ? `${address.slice(0, 8)}…${address.slice(-4)}` : address;
    }
    case "chain":
      return chainDisplayName(watch.chain);
    default:
      return watch.name ?? watch.catalogId;
  }
}

export const WATCH_KIND_LABELS: Record<WatchKind, string> = {
  protocol: "Protocol",
  address: "Address",
  entity: "Entity",
  tag: "Tag",
  label: "Label",
  group: "Group",
  chain: "Chain",
};

// ---------------------------------------------------------------------------
// Backlog: watch → search term
// ---------------------------------------------------------------------------

/** One lane of a `POST /transactions/search` request. */
export interface SearchTerm {
  kind: WatchKind;
  value: string;
  chains?: Caip2[];
}

/**
 * Watches one search request can carry, matching the server's `terms` cap.
 *
 * The server caps it because each term is at least one index query per page, so
 * an unbounded list turns one request into a fan-out the caller never sees the
 * cost of. The number is mirrored here so the scope editor can stop at the cap
 * instead of letting someone build an investigation whose feed then 400s — the
 * feed is one request now, so exceeding the cap fails the whole thing rather than
 * just the extra lane.
 */
export const MAX_SEARCH_TERMS = 25;

/**
 * The `POST /transactions/search` term that fetches this watch's backlog.
 *
 * The kinds line up one-for-one with {@link Watch}, which is the point of the
 * endpoint taking terms rather than filters: the server routes each term to the
 * index that can answer it, so the client no longer has to know that a protocol
 * watch is a `protocol_ids` filter while a tag watch is a `catalog_kind` pair.
 *
 * Chain narrowing is passed through as-is; empty means "every chain", the same
 * convention {@link watchChains} uses.
 *
 * The address is normalised here rather than left to the server, even though the
 * server normalises too. {@link searchTermLaneKey} has to reproduce the key the
 * server reports each lane's status under, and the server derives it from the
 * *canonical* value — so a checksummed address stored on a watch would key the
 * response one way and the lane chips another, and the chips would read zero.
 */
export function watchToSearchTerm(watch: Watch): SearchTerm {
  const chains = watchChains(watch);
  const narrow = chains.length ? { chains } : {};
  switch (watch.kind) {
    case "protocol":
      return { kind: "protocol", value: watch.protocolId, ...narrow };
    case "address": {
      const { chain, address } = parseAccount(watch.account);
      return { kind: "address", value: normalizeAddress(address, chain), ...narrow };
    }
    case "chain":
      return { kind: "chain", value: watch.chain };
    default:
      return { kind: watch.kind, value: watch.catalogId };
  }
}

/**
 * The per-lane identity the search response reports status under.
 *
 * Mirrors `TransactionSearchTerm.lane_key` on the server, which is what scopes
 * each lane's cursor. Duplicated across the language boundary because the
 * response's `sources[]` is keyed by it and there is nothing else to join a
 * lane's row count and error back to the watch that asked for it. The format is
 * pinned by a test on both sides; if they drift, lanes render as zero-count
 * rather than failing loudly, so keep them together.
 *
 * Note this keys on the *canonical* value the server echoes back, not the raw
 * one sent — an address is lower-cased and a chain resolves to CAIP-2 on the way
 * in, so joining on the request's own spelling would miss.
 */
export function searchTermLaneKey(term: SearchTerm): string {
  return `${term.kind}:${term.value}:${[...(term.chains ?? [])].sort().join(",")}`;
}

// ---------------------------------------------------------------------------
// Live tail: watch → stream-payload predicate
// ---------------------------------------------------------------------------

/** The subset of a published transaction payload the predicates read. */
interface StreamedRow {
  chain_id?: unknown;
  primary_protocol_id?: unknown;
  protocol_ids?: unknown;
  primary_entity_id?: unknown;
  participant_summary?: unknown;
  from?: unknown;
  to?: unknown;
}

function str(value: unknown): string | null {
  return typeof value === "string" && value ? value : null;
}

function strList(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === "string" && !!v) : [];
}

/**
 * Addresses a *streamed* row is known to involve.
 *
 * The published payload carries `participant_summary` (role counts plus the two
 * primary addresses), not the full participant list that the persisted row's
 * discovery index is built from. So the live tail sees an account only in the
 * sender/recipient position, while the backlog query matches it in any role —
 * a log-level counterparty appears on the next refetch rather than instantly.
 * Narrowing this would need the participant list on the wire, which is exactly
 * the fat payload `_ROW_PUBLISH_FIELDS` exists to prevent.
 */
function rowAddresses(row: StreamedRow): string[] {
  const summary = (row.participant_summary ?? {}) as Record<string, unknown>;
  return [str(summary.primary_from), str(summary.primary_to), str(row.from), str(row.to)].filter(
    (a): a is string => a !== null,
  );
}

/**
 * Whether a live streamed row belongs in this watch's feed.
 *
 * Catalog watches (`tag` / `label` / `group`) are **not decidable from the
 * wire**: an assignment lives in its own index and is not published on the row,
 * so this returns `false` for them rather than guessing. Those lanes stay
 * backlog-driven and pick up new rows on the next poll — a false negative that
 * delays a row, instead of a false positive that puts someone else's
 * transaction into a shared case file.
 */
export function matchesWatch(row: StreamedRow, watch: Watch): boolean {
  switch (watch.kind) {
    case "protocol": {
      const ids = new Set([str(row.primary_protocol_id), ...strList(row.protocol_ids)]);
      if (!ids.has(watch.protocolId)) return false;
      const chains = watchChains(watch);
      if (!chains.length) return true;
      const chain = toStoredChainId(str(row.chain_id));
      return chain ? chains.includes(chain) : false;
    }
    case "address": {
      const { chain, address } = parseAccount(watch.account);
      const rowChain = toStoredChainId(str(row.chain_id));
      const chains = watchChains(watch);
      if (chains.length) {
        if (!rowChain || !chains.includes(rowChain)) return false;
      } else if (chain && caip2Namespace(rowChain) !== caip2Namespace(chain)) {
        // Un-narrowed means namespace-wide, not chain-agnostic: an EVM address is
        // the same string on every `eip155` chain, but the identical string on a
        // different namespace is a different account.
        return false;
      }
      const wanted = normalizeAddress(address, rowChain ?? chain);
      return rowAddresses(row).some((a) => normalizeAddress(a, rowChain ?? chain) === wanted);
    }
    case "chain":
      return toStoredChainId(str(row.chain_id)) === watch.chain;
    case "entity":
      // The `primary_entity_id` rollup IS on the wire; explicit assignments are
      // not, so an assignment-only match arrives with the backlog.
      return str(row.primary_entity_id) === watch.catalogId;
    default:
      return false;
  }
}

/** Whether any watch in the scope accepts this row (an empty scope matches nothing). */
export function matchesAnyWatch(row: StreamedRow, watches: readonly Watch[]): boolean {
  return watches.some((watch) => matchesWatch(row, watch));
}

/** Whether the live tail can decide *anything* for this scope. */
export function hasLiveDecidableWatch(watches: readonly Watch[]): boolean {
  return watches.some((w) => w.kind !== "tag" && w.kind !== "label" && w.kind !== "group");
}

/**
 * Whether any lane still depends on the backlog poll, however live the tail is.
 *
 * The inverse of {@link hasLiveDecidableWatch} is not this: a scope can hold
 * both an address watch the stream serves and a tag watch it can't, and the
 * mixed case is the one that matters — slowing the poll because "the stream has
 * it covered" would silently delay exactly the lanes the stream never covers.
 *
 * `tag` / `label` / `group` are here because an assignment isn't on the
 * published payload at all. `entity` is here because only the
 * `primary_entity_id` rollup is: an explicit assignment to an entity is
 * invisible to {@link matchesWatch} and arrives with the backlog.
 */
export function needsBacklogPoll(watches: readonly Watch[]): boolean {
  return watches.some(
    (w) => w.kind === "tag" || w.kind === "label" || w.kind === "group" || w.kind === "entity",
  );
}

// ---------------------------------------------------------------------------
// Retention
// ---------------------------------------------------------------------------

/**
 * The stretch of `block_timestamp` a feed covers.
 *
 * Three cases rather than one preset list, because they behave differently and
 * the difference is load-bearing:
 *
 * - `rolling` — open-ended at the top. New transactions keep arriving, so the
 *   live stream applies and the floor moves as time passes.
 * - `absolute` — a closed historical window. Its contents cannot change, which is
 *   what makes it safe (and necessary) to stop the live stream and the poll: a
 *   transaction from today does not belong in a view of last March.
 * - `all` — no bound at all. Distinct from a very long rolling window because
 *   there is no floor to push to the server, so the query reads the top of the
 *   partition and paging is the only way further back.
 *
 * Preset vocabulary is reused from `@authe/fe-commons/metadata-analytics` so the
 * feed and the analytics views name the same spans the same way. Its
 * `resolveTimeRange` is deliberately NOT reused: it always returns both bounds,
 * which cannot express `all`, and it would turn a rolling window into a closed
 * one — silently killing the live tail.
 */
export type FeedWindow =
  | { kind: "rolling"; preset: TimeRangePreset }
  | { kind: "absolute"; since: number; until: number }
  | { kind: "all" };

export interface Retention {
  /** The stretch of block time the feed covers. */
  window: FeedWindow;
  /**
   * Cap on rows held from the live stream and the newest page, across the whole
   * feed rather than per chain or per watch.
   *
   * Pages the reader walked back to with "load older" sit on top of it (see
   * {@link effectiveRowCap}), because a button that fetches rows the trim then
   * discards is a button that does nothing. The newest page does NOT: it arrives
   * unasked, so it is what this cap is for.
   */
  maxRows: number;
  /**
   * Spend the cap round-robin across chains instead of strictly newest-first.
   *
   * Chains differ in throughput by orders of magnitude, so a strictly
   * newest-first cap is really a race: BNB at ~3s blocks fills the feed and the
   * Ethereum leg someone is actually waiting for never appears, even though the
   * case follows both. Round-robin gives each chain an equal slice of the cap and
   * hands back what a quiet chain does not use, so a single-chain feed is
   * unaffected and a busy chain is only ever throttled by the presence of
   * another one with something to show. See `balanceRowsByChain`.
   */
  balanceChains: boolean;
}

/** Rolling spans offered in the picker rail, newest-first. */
export const FEED_WINDOW_PRESETS: readonly TimeRangePreset[] = ["1h", "6h", "24h", "7d", "30d"];

export const WINDOW_ALL: FeedWindow = { kind: "all" };

export const DEFAULT_RETENTION: Retention = {
  window: { kind: "rolling", preset: "24h" },
  maxRows: 500,
  // On by default because it costs nothing on the common case: with one chain in
  // the feed it is exactly newest-first, and it only starts holding rows back
  // once a second chain has rows that would otherwise never be reached.
  balanceChains: true,
};

/** A closed window cannot gain rows, so nothing should be polling or streaming it. */
export function isClosedWindow(window: FeedWindow): boolean {
  return window.kind === "absolute";
}

/** Human label for the window, for the picker trigger and the feed header. */
export function feedWindowLabel(window: FeedWindow): string {
  if (window.kind === "all") return "Everything";
  if (window.kind === "rolling") return PRESET_LABELS[window.preset];
  return `${WINDOW_FMT.format(window.since * 1000)} – ${WINDOW_FMT.format(window.until * 1000)}`;
}

const WINDOW_FMT = new Intl.DateTimeFormat(undefined, {
  month: "short",
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

/**
 * The window's bounds in Unix **seconds**, `null` for unbounded on that side.
 *
 * Seconds, and via one helper rather than arithmetic at each call site, because
 * the units are a live hazard: `block_timestamp` is seconds on the API but
 * milliseconds from some ephemeral providers, and handing a millisecond value to
 * `since` asks for everything after the year 55000 — an empty feed that reads as
 * no activity rather than as a bug.
 */
export function windowBoundsSeconds(
  window: FeedWindow,
  now: number = Date.now(),
): { since: number | null; until: number | null } {
  switch (window.kind) {
    case "all":
      return { since: null, until: null };
    case "absolute":
      return { since: window.since, until: window.until };
    case "rolling":
      return {
        since: Math.floor(now / 1000) - PRESET_SECONDS[window.preset],
        // Deliberately open: a rolling window's top edge is "whatever arrives
        // next". Pinning it to now would exclude every row that lands after the
        // request was built, which is most of what a live feed is for.
        until: null,
      };
  }
}

/** Oldest `block_timestamp` (ms) a row may carry, or `null` for no floor. */
export function windowStartMs(window: FeedWindow, now: number = Date.now()): number | null {
  const { since } = windowBoundsSeconds(window, now);
  return since === null ? null : since * 1000;
}

/** Newest `block_timestamp` (ms) a row may carry, or `null` for no ceiling. */
export function windowEndMs(window: FeedWindow, now: number = Date.now()): number | null {
  const { until } = windowBoundsSeconds(window, now);
  return until === null ? null : until * 1000;
}

/**
 * The row cap to actually enforce, given how much history was explicitly loaded.
 *
 * `maxRows` bounds what the feed accumulates on its own — the live stream and the
 * newest page. It must not bound what the reader deliberately fetched: pressing
 * "load older" past the cap would drop the rows it just retrieved, so the button
 * would appear to do nothing and the count would sit still. Each loaded page
 * therefore buys room ON TOP of the retained head.
 *
 * `loadedOlder` counts only the pages walked back to. Counting the newest page
 * as well lets the head satisfy the cap by itself, and then the setting never
 * binds — the feed sits at whatever that page happened to return, which for a
 * multi-chain scope reads as the cap being applied per chain.
 */
export function effectiveRowCap(maxRows: number, loadedOlder: number): number {
  return maxRows + loadedOlder;
}

// ---------------------------------------------------------------------------
// Persisted investigation state (`SavedView.state.extra`)
// ---------------------------------------------------------------------------

export type InvestigationStatus = "open" | "archived";

export type { SceneOverlayDTO };

/** Cross-chain saga preset — seeds the workspace's live saga monitor. */
export type InvestigationSaga = "bridge.ccip" | "bridge.cctp" | null;

export interface InvestigationState {
  watches: Watch[];
  retention: Retention;
  /**
   * Which tenancy tier the feed reads from.
   *
   * NOT the same thing as who can see the case file — that is the saved view's
   * own `scope` (Personal / Shared). They were previously the same value, which
   * meant a shared case could only ever read the tenant partition. The tiers are
   * independent because the same transaction is stored once per tier: `user` and
   * `tenant` hold only what somebody imported there, while the ingest pipeline's
   * output lands in the shared `global` record — so a case following a protocol
   * or an entity across chains is nearly always asking about `global`.
   */
  feedScope: TxTenancyScopeId;
  status: InvestigationStatus;
  saga: InvestigationSaga;
  /**
   * The team's own reading of the graph: nodes the decode couldn't see, links
   * the chain doesn't record, relabels, and the noise they've hidden.
   *
   * Lives here rather than only in the workspace's Yjs doc because that doc is
   * a live cache — without a connected peer it survives neither a reload nor a
   * teammate opening the case tomorrow. Annotations are a conclusion, so they
   * belong to the case the same way its watches and pinned evidence do.
   *
   * Kept as the RAW field, deliberately: it holds one layer per SUBJECT, and a
   * case written before that carries a single flat body instead. Every reader
   * goes through `annotationLayers` / `layerForSubject`, which read both shapes,
   * so the migration is a read rule rather than a batch job — and a case nobody
   * opens keeps working untouched.
   */
  annotations: unknown;
  /**
   * Which table columns the case shows (`{ protocol: false }` — absent means
   * visible), or `null` while nobody has chosen and the focus default applies.
   *
   * Part of the case rather than the browser because a case is a team's shared
   * document: the columns someone put on screen to make an argument are part of
   * that argument, and a layout kept in `localStorage` means the teammate reading
   * the case tomorrow sees a different table than the one it was written against.
   * A reader without edit rights still gets to rearrange their own view — theirs
   * just stays local (see the detail page's `columnsShared`).
   */
  columnVisibility: Record<string, boolean> | null;
  /**
   * The pipeline this case's ingest runs through, or `null` for the system default.
   *
   * On the case rather than chosen per run because it is a property of the
   * INVESTIGATION, not of one address: a case following six wallets wants all six
   * decoded the same way, and six runs authored separately is six chances to pick
   * differently. A case whose findings came from two decode paths is one whose
   * feed cannot be compared with itself.
   *
   * `null` is a real answer, not a missing one — most cases genuinely want the
   * platform default, and storing an id for it would pin them to a definition
   * that can be renamed or deleted underneath them.
   */
  pipelineId: string | null;
  /**
   * The case's narrative — what happened, in order.
   *
   * Ordered by the array, not by a timestamp on each act: the sequence IS the
   * claim, and several acts can share a block. Empty for a case nobody has
   * written up, which is most of them — the feed, graph and flows all work
   * without it, and a case that is a monitor rather than a story never needs one.
   *
   * See `./caseActs`, particularly why each act names its own chains.
   */
  acts: CaseAct[];
  /** Last time a teammate touched the case (not the last matching transaction). */
  lastActivityAt: number;
}

export const DEFAULT_INVESTIGATION_STATE: InvestigationState = {
  watches: [],
  retention: DEFAULT_RETENTION,
  // `global` rather than the explorer's `tenant` default: an investigation
  // follows on-chain subjects, and the shared global record is where ingested
  // transactions land. Defaulting to `tenant` gives a new case an empty feed
  // until somebody imports rows into that tier by hand.
  feedScope: "global",
  status: "open",
  saga: null,
  annotations: {},
  columnVisibility: null,
  pipelineId: null,
  acts: [],
  lastActivityAt: 0,
};

// ---------------------------------------------------------------------------
// Templates
// ---------------------------------------------------------------------------

/** Which editor a template opens with, so the gallery asks for the right thing. */
export type TemplateSeedKind =
  | "protocol"
  | "address"
  | "catalog"
  | "saga"
  | "blank"
  /**
   * Seeded from a published incident in `commons.incidents` — the one seed kind
   * that fills in the TITLE, the WATCHES and the RETENTION at once, because the
   * registry already knows all three. See `./incidentSeed`.
   */
  | "incident";

export interface InvestigationTemplate {
  id: string;
  title: string;
  /** One line naming the actual job, so the gallery reads as use cases. */
  description: string;
  seedKind: TemplateSeedKind;
  saga: InvestigationSaga;
  /** Suggested case title prefix. */
  titleHint: string;
  /** Windows longer than the default where the work is retrospective. */
  retention?: Partial<Retention>;
}

export const INVESTIGATION_TEMPLATES: readonly InvestigationTemplate[] = [
  {
    id: "protocol-monitor",
    title: "Protocol monitor",
    description: "Every transaction in and out of one or more protocols, as it lands.",
    seedKind: "protocol",
    saga: null,
    titleHint: "Protocol monitor",
  },
  {
    id: "address-trail",
    title: "Address trail",
    description:
      "Follow accounts across chains — funds moving after an exploit, or an insider's positions.",
    seedKind: "address",
    saga: null,
    titleHint: "Address trail",
    retention: { window: { kind: "rolling", preset: "7d" } },
  },
  {
    id: "entity-watch",
    title: "Entity / org watch",
    description: "Everything touching a catalog entity: your own addresses, or a counterparty's.",
    seedKind: "catalog",
    saga: null,
    titleHint: "Entity watch",
    retention: { window: { kind: "rolling", preset: "7d" } },
  },
  {
    id: "cross-chain-saga",
    title: "Cross-chain saga",
    description: "Watch both legs of bridged messages and catch the ones whose destination fails.",
    seedKind: "saga",
    saga: "bridge.ccip",
    titleHint: "CCIP sagas",
  },
  {
    id: "incident",
    title: "Published incident",
    description:
      "Start from a reported exploit — its addresses, dates and sources, already filled in.",
    seedKind: "incident",
    saga: null,
    titleHint: "Incident",
    // Deliberately absent: the retention comes from the incident's own date via
    // `incidentRetention`, which is the whole point of this template. A default
    // here would be a 24h window over an exploit from last month — an empty feed
    // and no clue why.
  },
  {
    id: "blank",
    title: "Blank case",
    description: "Start empty and add what you are following as you go.",
    seedKind: "blank",
    saga: null,
    titleHint: "Investigation",
  },
] as const;

export function investigationTemplate(id: string): InvestigationTemplate | null {
  return INVESTIGATION_TEMPLATES.find((t) => t.id === id) ?? null;
}
